import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  orderBy,
  startAfter,
  limit as firestoreLimit,
  DocumentData,
  Timestamp,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/config/constants";
import { Bar, BarStatus, BarVisibility } from "@/types";
import ngeohash from "ngeohash";

/** Geohash-Präzision für die Umkreissuche (~1,2 km Zelle → ~3,7 km Abdeckung mit Nachbarn). */
const NEARBY_GEOHASH_PRECISION = 6;

const distanceMeters = (
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number => {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const lat1 = (aLat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export type NearbyBar = Bar & { distanceMeters: number };

const getNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const normalizeBar = (doc: DocumentData): Bar => {
  const data = doc.data();
  const location = data.location as { latitude?: number; longitude?: number } | undefined;
  const latitude = getNumber(data.latitude) ?? getNumber(location?.latitude);
  const longitude = getNumber(data.longitude) ?? getNumber(location?.longitude);

  return {
    id: doc.id,
    ...data,
    ...(latitude !== undefined ? { latitude } : {}),
    ...(longitude !== undefined ? { longitude } : {}),
  } as Bar;
};

const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getBarAddressText = (bar: Partial<Bar>): string => {
  if (typeof bar.address === "string") return bar.address;
  if (!bar.address || typeof bar.address !== "object") return "";

  const data = bar.address as Record<string, unknown>;
  return [
    data.street,
    data.houseNumber,
    data.zip,
    data.postalCode,
    data.city,
  ]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .map((part) => String(part).trim())
    .join(" ");
};

/**
 * Bars Service
 * Handles all Firestore operations related to bars
 */
export const barsService = {
  /**
   * Get all bars with optional filters and cursor-based pagination
   */
  getBars: async (filters?: {
    status?: BarStatus;
    isVerified?: boolean;
    isClaimed?: boolean;
    limit?: number;
    startAfterTimestamp?: Timestamp;
  }): Promise<Bar[]> => {
    try {
      const barsRef = collection(db, COLLECTIONS.BARS);
      let q = query(barsRef, orderBy("createdAt", "desc"));

      if (filters?.status) {
        q = query(q, where("status", "==", filters.status));
      }
      if (filters?.isVerified !== undefined) {
        q = query(q, where("isVerified", "==", filters.isVerified));
      }
      if (filters?.isClaimed !== undefined) {
        q = query(q, where("isClaimed", "==", filters.isClaimed));
      }
      if (filters?.startAfterTimestamp) {
        q = query(q, startAfter(filters.startAfterTimestamp));
      }
      // Default page size 100, max 200
      const pageSize = Math.min(filters?.limit ?? 100, 200);
      q = query(q, firestoreLimit(pageSize));

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc: DocumentData) => normalizeBar(doc));
    } catch (error) {
      console.error("Error fetching bars:", error);
      throw new Error("Fehler beim Laden der Bars");
    }
  },

  /**
   * Get single bar by ID
   */
  getBar: async (barId: string): Promise<Bar | null> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      const barDoc = await getDoc(barRef);

      if (!barDoc.exists()) {
        return null;
      }

      return normalizeBar(barDoc as DocumentData);
    } catch (error) {
      console.error("Error fetching bar:", error);
      throw new Error("Fehler beim Laden der Bar");
    }
  },

  /**
   * Search bars by name or city.
   * Server-side: lädt maximal 200 Bars (name-sortiert) und bewertet client-side.
   * Für echte Volltextsuche wäre Algolia/Typesense empfohlen.
   */
  searchBars: async (searchTerm: string, limitCount = 20): Promise<Bar[]> => {
    try {
      const normalizedTerm = normalizeSearchText(searchTerm);
      if (!normalizedTerm) return [];

      const barsRef = collection(db, COLLECTIONS.BARS);
      // Limit auf 200 — verhindert unbegrenzten Collection-Read
      const snapshot = await getDocs(
        query(barsRef, orderBy("name"), firestoreLimit(200)),
      );

      const bars = snapshot.docs.map((doc: DocumentData) => normalizeBar(doc));

      const queryTokens = normalizedTerm.split(" ").filter((token) => token.length >= 2);

      return bars
        .map((bar) => {
          const searchable = normalizeSearchText(
            [
              bar.name,
              bar.city ?? "",
              bar.postalCode ?? "",
              getBarAddressText(bar),
            ].join(" "),
          );

          let score = 0;
          if (normalizeSearchText(bar.name).startsWith(normalizedTerm)) score += 90;
          if (normalizeSearchText(bar.name).includes(normalizedTerm)) score += 60;
          if (searchable.includes(normalizedTerm)) score += 40;

          const tokenHits = queryTokens.reduce(
            (count, token) => count + (searchable.includes(token) ? 1 : 0),
            0,
          );
          score += tokenHits * 20;

          return { bar, score };
        })
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, limitCount)
        .map((entry) => entry.bar);
    } catch (error) {
      console.error("Error searching bars:", error);
      throw new Error("Fehler bei der Suche");
    }
  },

  /**
   * Findet bestehende Bars im Umkreis einer Koordinate (für Dubletten-Erkennung).
   * Nutzt eine Geohash-Zelle + ihre 8 Nachbarn und filtert anschließend exakt
   * per Haversine-Distanz. Bars ohne geohash/Koordinaten werden ignoriert.
   */
  getBarsNear: async (
    latitude: number,
    longitude: number,
    options?: { radiusMeters?: number; limit?: number },
  ): Promise<NearbyBar[]> => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
    const radiusMeters = options?.radiusMeters ?? 2000;
    const max = options?.limit ?? 15;

    try {
      const center = ngeohash.encode(latitude, longitude, NEARBY_GEOHASH_PRECISION);
      const cells = [center, ...ngeohash.neighbors(center)];
      const barsRef = collection(db, COLLECTIONS.BARS);

      const snapshots = await Promise.all(
        cells.map((cell) =>
          getDocs(
            query(
              barsRef,
              where("geohash", ">=", cell),
              where("geohash", "<", cell + ""),
              firestoreLimit(50),
            ),
          ),
        ),
      );

      const seen = new Set<string>();
      const results: NearbyBar[] = [];
      for (const snapshot of snapshots) {
        for (const docSnap of snapshot.docs) {
          if (seen.has(docSnap.id)) continue;
          seen.add(docSnap.id);
          const bar = normalizeBar(docSnap as DocumentData);
          if (bar.latitude === undefined || bar.longitude === undefined) continue;
          const meters = distanceMeters(latitude, longitude, bar.latitude, bar.longitude);
          if (meters <= radiusMeters) results.push({ ...bar, distanceMeters: meters });
        }
      }

      return results
        .sort((a, b) => a.distanceMeters - b.distanceMeters)
        .slice(0, max);
    } catch (error) {
      console.error("Error fetching nearby bars:", error);
      throw new Error("Fehler beim Suchen naher Bars");
    }
  },

  /**
   * Update bar verification status
   */
  updateVerificationStatus: async (
    barId: string,
    isVerified: boolean,
    adminUid: string,
  ): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      await updateDoc(barRef, {
        isVerified,
        verifiedAt: isVerified ? serverTimestamp() : null,
        verifiedBy: isVerified ? adminUid : null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating verification status:", error);
      throw new Error("Fehler beim Aktualisieren des Verifizierungsstatus");
    }
  },

  /**
   * Update bar status
   */
  updateBarStatus: async (barId: string, status: BarStatus): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      await updateDoc(barRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating bar status:", error);
      throw new Error("Fehler beim Aktualisieren des Status");
    }
  },

  /**
   * Update bar visibility
   */
  updateBarVisibility: async (
    barId: string,
    visibility: BarVisibility,
  ): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      await updateDoc(barRef, {
        visibility,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating bar visibility:", error);
      throw new Error("Fehler beim Aktualisieren der Sichtbarkeit");
    }
  },

  /**
   * Update bar data (admin corrections)
   */
  updateBar: async (barId: string, updates: Partial<Bar>): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      await updateDoc(barRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating bar:", error);
      throw new Error("Fehler beim Aktualisieren der Bar-Daten");
    }
  },

  /**
   * Delete bar (admin only, use with caution)
   */
  deleteBar: async (barId: string): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      await deleteDoc(barRef);
      // Note: Consider implementing soft delete instead
      // or handling subcollections (drinks, events, offers) deletion
    } catch (error) {
      console.error("Error deleting bar:", error);
      throw new Error("Fehler beim Löschen der Bar");
    }
  },

  /**
   * Get bars statistics — verwendet getCountFromServer statt getDocs
   */
  getBarsStats: async (): Promise<{
    total: number;
    active: number;
    verified: number;
    claimed: number;
  }> => {
    try {
      const barsRef = collection(db, COLLECTIONS.BARS);

      const [totalCount, activeCount, verifiedCount, claimedCount] =
        await Promise.all([
          getCountFromServer(barsRef),
          getCountFromServer(query(barsRef, where("status", "==", "active"))),
          getCountFromServer(query(barsRef, where("isVerified", "==", true))),
          getCountFromServer(query(barsRef, where("isClaimed", "==", true))),
        ]);

      return {
        total: totalCount.data().count,
        active: activeCount.data().count,
        verified: verifiedCount.data().count,
        claimed: claimedCount.data().count,
      };
    } catch (error) {
      console.error("Error fetching bars stats:", error);
      throw new Error("Fehler beim Laden der Bar-Statistiken");
    }
  },

  /**
   * Gibt alle Bars zurück, die ein User jemals als Owner hatte.
   * Quelle: claimRequests collection (approved claims = Ownership-History).
   */
  getBarOwnerHistory: async (
    userId: string,
  ): Promise<
    {
      barId: string;
      barName: string;
      claimedAt: import("firebase/firestore").Timestamp | null;
      claimId: string;
    }[]
  > => {
    try {
      const claimsRef = collection(db, "claimRequests");
      const snapshot = await getDocs(
        query(
          claimsRef,
          where("userId", "==", userId),
          where("status", "==", "approved"),
        ),
      );
      const results: {
        barId: string;
        barName: string;
        claimedAt: import("firebase/firestore").Timestamp | null;
        claimId: string;
      }[] = [];
      for (const claimDoc of snapshot.docs) {
        const data = claimDoc.data();
        // Bar-Name nachschlagen
        let barName = data.barName || data.barId;
        try {
          const barDoc = await getDoc(doc(db, "bars", data.barId));
          if (barDoc.exists()) barName = barDoc.data().name || barName;
        } catch {
          /* ignore */
        }
        results.push({
          claimId: claimDoc.id,
          barId: data.barId,
          barName,
          claimedAt: data.reviewedAt ?? data.requestedAt ?? null,
        });
      }
      return results;
    } catch (error) {
      console.error("Error fetching bar owner history:", error);
      throw new Error("Fehler beim Laden der Ownership-History");
    }
  },

  /**
   * Zählt Inhalte einer Bar (Drinks, Events, Offers) – nützlich vor Content-Transfer-Entscheidung.
   */
  getBarContentCount: async (
    barId: string,
  ): Promise<{ drinks: number; events: number; offers: number }> => {
    try {
      const barRef = doc(db, "bars", barId);
      const [drinksSnap, eventsSnap, offersSnap] = await Promise.all([
        getDocs(collection(barRef, "drinks")),
        getDocs(collection(barRef, "events")),
        getDocs(collection(barRef, "offers")),
      ]);
      return {
        drinks: drinksSnap.size,
        events: eventsSnap.size,
        offers: offersSnap.size,
      };
    } catch (error) {
      console.error("Error counting bar content:", error);
      return { drinks: 0, events: 0, offers: 0 };
    }
  },

  /**
   * Löscht alle Subcollection-Inhalte einer Bar (drinks, events, offers).
   * Wird beim Content-Transfer genutzt wenn der neue Owner alles neu aufsetzen will.
   */
  deleteBarContent: async (barId: string): Promise<void> => {
    try {
      const barRef = doc(db, "bars", barId);
      const subcollections = ["drinks", "events", "offers"];
      for (const sub of subcollections) {
        const snap = await getDocs(collection(barRef, sub));
        if (snap.empty) continue;
        // Batch deletes (max 500 per batch)
        const chunks: DocumentData[][] = [];
        let chunk: DocumentData[] = [];
        for (const d of snap.docs) {
          chunk.push(d);
          if (chunk.length === 499) {
            chunks.push(chunk);
            chunk = [];
          }
        }
        if (chunk.length) chunks.push(chunk);
        for (const c of chunks) {
          const batch = writeBatch(db);
          c.forEach((d) => batch.delete(d.ref));
          await batch.commit();
        }
      }
    } catch (error) {
      console.error("Error deleting bar content:", error);
      throw new Error("Fehler beim Löschen der Bar-Inhalte");
    }
  },

  /**
   * Weist eine Bar einem (ehemaligen) Owner neu zu und startet seinen Trial neu.
   * Wird aus der Trial-Management-Seite aufgerufen.
   */
  reassignBarOwner: async (
    barId: string,
    userId: string,
    ownerName: string,
    ownerEmail: string,
    adminUid: string,
    trialEndDate: Date,
  ): Promise<void> => {
    try {
      const now = (await import("firebase/firestore")).Timestamp.now();
      const { Timestamp: TS } = await import("firebase/firestore");
      const trialEndTs = TS.fromDate(trialEndDate);
      const batch = writeBatch(db);
      // Bar aktualisieren – update statt set+merge, damit die Firestore-Regel
      // für update (isAdmin) greift und nicht die strikteren create-Regeln.
      // reassignBarOwner wird nur für Bars aufgerufen, die bereits einen Owner
      // hatten, also existiert das Dokument immer.
      batch.update(doc(db, "bars", barId), {
        ownerId: userId,
        ownerName,
        ownerEmail,
        isClaimed: true,
        claimedAt: now,
        reAssignedAt: now,
        reAssignedBy: adminUid,
        updatedAt: now,
      });
      // User-Subscription reaktivieren (beide Strukturen)
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      const currentBars: string[] = userSnap.exists()
        ? (userSnap.data()?.owner?.ownedBars ?? [])
        : [];
      const updatedBars = currentBars.includes(barId)
        ? currentBars
        : [...currentBars, barId];
      batch.update(userRef, {
        "owner.isOwner": true,
        "owner.ownedBars": updatedBars,
        hasActiveSubscription: true,
        subscriptionType: "trial",
        subscriptionEndDate: trialEndTs,
        "subscription.isTrialActive": true,
        "subscription.trialEndDate": trialEndTs,
        "subscription.tier": "trial",
        updatedAt: now,
      });
      await batch.commit();
    } catch (error) {
      console.error("Error reassigning bar owner:", error);
      throw new Error("Fehler beim Neuzuweisen der Bar");
    }
  },

  /**
   * Entzieht einem Owner eine Bar und setzt sie in den Community-Zustand zurück.
   * Die Bar kann danach wieder von der Community bearbeitet bzw. später neu geclaimt werden.
   */
  resetBarToCommunity: async (
    barId: string,
    userId: string,
    adminUid: string,
  ): Promise<void> => {
    try {
      const now = Timestamp.now();

      const barRef = doc(db, "bars", barId);
      const nestedCollections = ["drinks", "events", "offers", "stories"] as const;
      for (const nestedCollection of nestedCollections) {
        const snap = await getDocs(collection(barRef, nestedCollection));
        if (snap.empty) continue;

        let chunk: DocumentData[] = [];
        for (const d of snap.docs) {
          chunk.push(d);
          if (chunk.length === 450) {
            const deleteBatch = writeBatch(db);
            chunk.forEach((docSnap) => deleteBatch.delete(docSnap.ref));
            await deleteBatch.commit();
            chunk = [];
          }
        }

        if (chunk.length > 0) {
          const deleteBatch = writeBatch(db);
          chunk.forEach((docSnap) => deleteBatch.delete(docSnap.ref));
          await deleteBatch.commit();
        }
      }

      const auxiliaryCollections = ["claimRequests", "contentReports"] as const;
      for (const collectionName of auxiliaryCollections) {
        const fieldNames = collectionName === "claimRequests"
          ? ["barId"]
          : ["barId", "entityId"];

        for (const fieldName of fieldNames) {
          const snap = await getDocs(
            query(collection(db, collectionName), where(fieldName, "==", barId)),
          );

          if (snap.empty) continue;

          let chunk: DocumentData[] = [];
          for (const d of snap.docs) {
            chunk.push(d);
            if (chunk.length === 450) {
              const deleteBatch = writeBatch(db);
              chunk.forEach((docSnap) => deleteBatch.delete(docSnap.ref));
              await deleteBatch.commit();
              chunk = [];
            }
          }

          if (chunk.length > 0) {
            const deleteBatch = writeBatch(db);
            chunk.forEach((docSnap) => deleteBatch.delete(docSnap.ref));
            await deleteBatch.commit();
          }
        }
      }

      const batch = writeBatch(db);

      batch.update(barRef, {
        ownerId: null,
        ownerName: null,
        ownerEmail: null,
        ownerPhone: null,
        isClaimed: false,
        claimedAt: null,
        reAssignedAt: null,
        reAssignedBy: null,
        contentPurged: true,
        contentPurgedAt: now,
        claimStatus: null,
        drinkCount: 0,
        eventCount: 0,
        offerCount: 0,
        storyCount: 0,
        adminBlocked: false,
        status: "active",
        visibility: "public",
        ownerManagedDescription: null,
        ownerManagedOpeningHours: null,
        amenities: [],
        audiences: [],
        atmospheres: [],
        priceHighlights: null,
        website: null,
        contactEmail: null,
        contactPhone: null,
        galleryImageUrls: [],
        upcomingEventCount: 0,
        lastEventUpdate: null,
        updatedAt: now,
        ownershipResetAt: now,
        ownershipResetBy: adminUid,
        "mapMarker.isClaimed": false,
        "mapMarker.ownerId": null,
        "mapMarker.status": "active",
        "mapMarker.visibility": "public",
        "mapMarker.hasPriceData": false,
        "mapMarker.availableDrinkCategories": [],
        "mapMarker.amenities": [],
        "mapMarker.audiences": [],
        "mapMarker.atmospheres": [],
        "mapMarker.openingHours": null,
        "mapMarker.contentPurged": true,
      });

      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      const currentBars: string[] = userSnap.exists()
        ? (userSnap.data()?.owner?.ownedBars ?? [])
        : [];
      const updatedBars = currentBars.filter((ownedBarId) => ownedBarId !== barId);

      batch.update(userRef, {
        "owner.ownedBars": updatedBars,
        "owner.isOwner": updatedBars.length > 0,
        hasActiveSubscription: updatedBars.length > 0,
        subscriptionType: updatedBars.length > 0 ? "trial" : "free",
        subscriptionEndDate: updatedBars.length > 0
          ? (userSnap.data()?.subscriptionEndDate ?? null)
          : null,
        "subscription.isTrialActive": updatedBars.length > 0,
        "subscription.trialEndDate": updatedBars.length > 0
          ? (userSnap.data()?.subscription?.trialEndDate ?? userSnap.data()?.subscriptionEndDate ?? null)
          : null,
        "subscription.tier": updatedBars.length > 0 ? "trial" : "free",
        "subscription.status": updatedBars.length > 0 ? "trial" : "cancelled",
        updatedAt: now,
      });

      await batch.commit();
    } catch (error) {
      console.error("Error resetting bar to community:", error);
      throw new Error("Fehler beim Zurücksetzen der Bar auf Community");
    }
  },
};
